import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Video, Clock, CheckSquare, Youtube, Music, Radio, ArrowRight, Sparkles, Heart, Users, Play, HelpCircle } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

// FAQ data for schema markup
const faqData = [
  {
    question: "What happened to Studyverse?",
    answer: "Studyverse has been shut down, leaving students searching for alternative platforms. CSW (Chill Study Work) is the best free Studyverse alternative with Study Stream, Study Timer, and Task Management features."
  },
  {
    question: "Is CSW a free Studyverse alternative?",
    answer: "Yes, CSW is completely free to use with no limits. You can join unlimited study streams, use the Pomodoro timer, and manage tasks without any subscription fees."
  },
  {
    question: "What features does CSW offer compared to Studyverse?",
    answer: "CSW offers Study Stream for real-time study sessions, customizable Study Timer with Pomodoro technique, Task Management with Kanban boards, synchronized YouTube watching, and lo-fi music playlists."
  },
  {
    question: "Can I study with others on CSW like Studyverse?",
    answer: "Absolutely! CSW's Study Stream feature lets you join or host live study sessions with students worldwide, creating a sense of companionship and accountability."
  },
  {
    question: "Does CSW have a study timer like Studyverse?",
    answer: "Yes, CSW has a fully customizable study timer with visual and audio cues, adjustable intervals for work and breaks, and customizable backgrounds."
  },
  {
    question: "Is CSW better than Studyverse?",
    answer: "CSW offers more features than Studyverse including unlimited study streams, task management, quizzes, flash cards, and K-Pop themed study rooms, all completely free."
  }
];

const StudyVerse = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Studyverse Alternative - Free Study Rooms"
        description="Looking for a Studyverse alternative? Study Verse is down but CSW offers free Study Stream, Study Timer, and Task Management. Start studying smarter today."
        keywords="studyverse alternative, studyverse, study verse, online study platform, virtual study room, study together online"
        canonicalUrl="/studyverse/"
      />
      
      {/* BreadcrumbList Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://csw.live/" },
          { "@type": "ListItem", "position": 2, "name": "Alternatives", "item": "https://csw.live/alternatives/" },
          { "@type": "ListItem", "position": 3, "name": "Studyverse Alternative", "item": "https://csw.live/studyverse/" }
        ]
      })}} />

      {/* WebPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Studyverse Alternative - Free Online Study Rooms & Study Stream | CSW",
        "description": "Looking for a Studyverse alternative? Study Verse is down but CSW offers free Study Stream, Study Timer, and Task Management. Join online study sessions with students worldwide.",
        "url": "https://csw.live/studyverse/",
        "inLanguage": "en",
        "dateModified": "2025-01-17",
        "isPartOf": {
          "@type": "WebSite",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live"
        },
        "about": {
          "@type": "Thing",
          "name": "Studyverse Alternative",
          "description": "Free alternative to Studyverse for online study sessions and virtual study rooms"
        },
        "mainEntity": {
          "@type": "SoftwareApplication",
          "name": "CSW - Chill Study Work"
        },
        "mentions": [
          { "@type": "Thing", "name": "Studyverse" },
          { "@type": "Thing", "name": "Study Verse" },
          { "@type": "Thing", "name": "Online Study" },
          { "@type": "Thing", "name": "Study Stream" },
          { "@type": "Thing", "name": "Virtual Study Room" },
          { "@type": "Thing", "name": "Study With Me" },
          { "@type": "Thing", "name": "Pomodoro Timer" },
          { "@type": "Thing", "name": "Study Timer" },
          { "@type": "Thing", "name": "Task Management" },
          { "@type": "Thing", "name": "Group Study" },
          { "@type": "Thing", "name": "Live Study Session" },
          { "@type": "Thing", "name": "Focus Timer" }
        ],
        "keywords": "studyverse, studyverse alternative, study verse, online study, study stream, virtual study room, study together online, study with me, live study session, pomodoro timer, study timer, task management, group study, focus timer, online study platform, study room online, study buddy online, accountability partner, productivity app, study motivation, exam preparation, student productivity, remote study, co-working online, body doubling study"
      })}} />
      
      {/* SoftwareApplication Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - Studyverse Alternative | Online Study & Study Stream Platform",
        "alternateName": ["Chill Study Work", "CSW Study", "Studyverse Replacement"],
        "applicationCategory": "EducationalApplication",
        "applicationSubCategory": "Online Study Platform",
        "operatingSystem": "Web Browser",
        "description": "Free Studyverse alternative with Study Stream, Study Timer, and Task Management. Join unlimited online study sessions with students worldwide. No time limits, no fees.",
        "url": "https://csw.live/studyverse/",
        "downloadUrl": "https://app.csw.live",
        "softwareVersion": "2.0",
        "releaseNotes": "Now with enhanced Study Stream, improved Study Timer, and Kanban task management",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "2500",
          "reviewCount": "1800",
          "bestRating": "5",
          "worstRating": "1"
        },
        "featureList": [
          "Free Study Stream - Live video study sessions",
          "Study With Me - Join global study community",
          "Online Study Rooms - Virtual co-working spaces",
          "Customizable Study Timer - Pomodoro technique",
          "Task Management - Kanban boards and to-do lists",
          "Group Study - Collaborative study sessions",
          "Synchronized YouTube Watching",
          "Lo-fi Music Playlist - Focus-enhancing ambient sounds",
          "No Time Limits - Study as long as you need",
          "Study Statistics - Track your progress",
          "Custom Backgrounds - Personalize your study space",
          "Break Reminders - Stay healthy while studying"
        ],
        "screenshot": "https://csw.live/videos/productivity-demo.mp4",
        "publisher": {
          "@type": "Organization",
          "name": "CSW - Chill Study Work",
          "url": "https://csw.live",
          "logo": "https://csw.live/favicon.ico"
        },
        "audience": {
          "@type": "EducationalAudience",
          "educationalRole": "student",
          "audienceType": "Students, Remote Workers, Lifelong Learners"
        },
        "keywords": "studyverse, online study, study stream, virtual study room, study with me, pomodoro, focus timer, study together, group study, productivity"
      })}} />

      {/* Organization Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "CSW - Chill Study Work",
        "url": "https://csw.live",
        "logo": "https://csw.live/favicon.ico",
        "description": "Free online study platform offering Study Stream, Study Timer, and Task Management. The best Studyverse alternative for students worldwide.",
        "sameAs": [
          "https://app.csw.live"
        ],
        "knowsAbout": ["Online Study", "Study Stream", "Studyverse", "Virtual Study Rooms", "Pomodoro Technique", "Task Management", "Group Study"]
      })}} />
      
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      })}} />
      
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in">
              <span className="text-gradient">Studyverse</span>
            </h1>
            
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Studyverse Alternative
            </h1>
            
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight animate-fade-in text-muted-foreground" style={{ animationDelay: '0.2s' }}>
              Be Productive In Less Than{" "}
              <span className="text-gradient">10 Seconds</span>
            </h2>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              At CSW, we revolutionize the way you study by creating a virtual study room for online learners worldwide. Whether you're preparing for exams, working on projects, or just need a productive space, CSW is your go-to platform for <Link to="/study-with-me/" className="text-primary hover:underline">studying together</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium">Free to Use</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-medium">No Limits</span>
                </div>
              </div>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* StudyVerse Shutdown Notice */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-primary/20">
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                    Study Verse Is <span className="text-gradient">Down</span>
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    It's sad news that Study Verse has been shut down, leaving many students searching for a new place to focus and collaborate. But don't worry—<span className="font-semibold text-foreground">CSW – Chill Study Work</span> is here to provide a better alternative. <Link to="/studyverse/history/" className="text-primary hover:underline font-medium">Read the full Studyverse story →</Link>
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    We are currently in beta, but we're already meeting your study needs with a range of innovative features designed to enhance your learning experience. Explore our <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link>, <Link to="/productivity-tool/" className="text-primary hover:underline">Productivity Tools</Link>, and various <Link to="/study-with-friends/" className="text-primary hover:underline">study modes</Link> to find what works best for you.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Study Stream Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-xs font-medium text-secondary">
                NO LIMIT
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study <span className="text-gradient">Stream</span>
              </h2>
              <p className="text-xl text-muted-foreground font-light">
                Don't pay to be productive. Join unlimited streams, anytime.
              </p>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Engage in real-time study sessions with our <Link to="/study-stream/" className="text-primary hover:underline">live streaming feature</Link>. Share your study moments with others and create a sense of companionship. Our <Link to="/study-with-me/" className="text-primary hover:underline">Study With Me</Link> live streams allow you to join or host study sessions, providing a structured and motivating environment.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Whether you're working on a <Link to="/group-study/" className="text-primary hover:underline">group project</Link>, preparing for an exam, or simply need the silent company of fellow learners, live streaming keeps you accountable and inspired. Learn how <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="text-primary hover:underline">turning on your camera can boost productivity</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Video className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Live Study Stream Interface</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Study Timer Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 order-2 lg:order-1 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <Clock className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Customizable Study Timer</p>
            </Card>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study <span className="text-gradient">Timer</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Our customizable <Link to="/study-timer/" className="text-primary hover:underline">study timer</Link> is designed to help you stay focused and productive. You can adjust the timer to fit your unique study sessions, set specific intervals for work and breaks, and change the background to suit your mood.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  The study timer helps you manage your time effectively, ensuring you stay on task and make the most out of every study session. With visual and audio cues, you'll never miss a break or overrun a study period. Discover more <Link to="/blog/top-10-digital-tools-for-academic-success/" className="text-primary hover:underline">digital tools for academic success</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Task Management Feature */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Task <span className="text-gradient">Management</span>
              </h2>
              <div className="space-y-4">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Boost your productivity with our <Link to="/task-management/" className="text-primary hover:underline">task management tool</Link>. Create, organize, and complete tasks to keep track of your academic or work-related goals. With the ability to set deadlines, prioritize tasks, and track your progress, you'll stay on top of your responsibilities.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  This feature helps you stay organized and focused, ensuring nothing falls through the cracks. You can break down larger projects into manageable tasks, set <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>, and monitor your progress. Check out <Link to="/blog/how-to-stop-procrastination/" className="text-primary hover:underline">how to stop procrastination</Link> and learn about <Link to="/blog/apps-boost-organization-college/" className="text-primary hover:underline">apps that boost organization in college</Link>.
                </p>
              </div>
            </div>
            <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-16 h-16 text-muted-foreground/50" />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Task Management Dashboard</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                More <span className="text-gradient">Features</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Everything you need for a complete study experience
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Youtube className="w-7 h-7 text-primary" />,
                  title: "Watch YouTube Together",
                  desc: "Enhance your study breaks with our synchronized YouTube-watching feature. Watch educational videos, listen to music, or catch up on the latest content with friends."
                },
                {
                  icon: <Music className="w-7 h-7 text-primary" />,
                  title: "Listen To Music",
                  desc: "Boost your concentration with our integrated music player. Listen to your favorite tunes while you work, with the timer running to keep you on track."
                },
                {
                  icon: <Radio className="w-7 h-7 text-primary" />,
                  title: "Lofi Music Playlist",
                  desc: "Immerse yourself in a calming study environment with our lo-fi music option. Designed with a dark mode theme for a relaxed atmosphere."
                },
              ].map((item, index) => (
                <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                    {item.icon}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary">
                <HelpCircle className="w-3 h-3" />
                FAQ
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Studyverse <span className="text-gradient">FAQ</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Common questions about Studyverse and CSW as an alternative
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl px-6 data-[state=open]:border-primary/30"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study <span className="text-gradient">Smarter?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of students who have found their perfect study space with CSW. Explore the <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline">science behind Study With Me</Link> and start your productive journey today.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <RelatedAlternatives currentPlatform="Studyverse" />

      <Footer />
    </div>
  );
};

export default StudyVerse;